import { trpc } from "@/lib/trpc";
import { messageValidators } from "@shared/validators/message-validators";
import { searchValidators } from "@shared/validators/search-validators";
import { useRef, useEffect } from "react";
import { z } from "zod";

interface UseInfiniteScrollOptions {
    threadId?: string;
    observer?: IntersectionObserverInit;
    enabled?: boolean;
    filter?: z.infer<typeof messageValidators.messageFilterSchema>;
    limit?: number;
}

export const useMessagesQuery = (
    threadId?: string,
    filter?: z.infer<typeof messageValidators.messageFilterSchema>,
    limit?: number,
    enabled?: boolean
) => {
    return trpc.messages.findMany.useInfiniteQuery(
        {
            filter,
            limit,
            threadId,
        },
        {
            enabled,
            getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
            // select: (data) => ({
            //     pages: [...data.pages].reverse(),
            //     pageParams: [...data.pageParams].reverse(),
            // }),
        }
    );
};
export function useInfiniteScrollMessages({
    enabled = true,
    observer,
    threadId,
    filter,
    limit,
}: UseInfiniteScrollOptions) {
    const query = useMessagesQuery(threadId, filter, limit, enabled);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!bottomRef.current) return;

        const obs = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (
                entry.isIntersecting &&
                query.hasNextPage &&
                !query.isLoading &&
                !query.isFetchingNextPage // ← don’t double‐fetch
            ) {
                query.fetchNextPage();
            }
        }, observer);

        obs.observe(bottomRef.current);
        return () => obs.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bottomRef, query.hasNextPage, query.fetchNextPage, observer, filter]);
    const items = query.data?.pages?.flatMap((page) => page.data) ?? [];
    return {
        ...query,
        items,
        bottomRef,
    };
}

export const useSearchMessagesAndThreads = (filters: z.infer<typeof searchValidators.searchFilterSchema>, enabled?: boolean) => {
    return trpc.search.search.useQuery(
        filters,
        {
            enabled,
        }
    );
}